import { useCallback, useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

let placesPromise: Promise<google.maps.PlacesLibrary> | null = null;

function loadPlaces(): Promise<google.maps.PlacesLibrary> {
  if (!placesPromise) {
    setOptions({ key: API_KEY ?? '', v: 'weekly' });
    placesPromise = importLibrary('places') as Promise<google.maps.PlacesLibrary>;
  }
  return placesPromise;
}

interface Props {
  value: string;
  onChange: (address: string) => void;
  onPlaceSelect?: (lat: number, lng: number, address: string) => void;
  placeholder?: string;
}

export function AddressAutocomplete({ value, onChange, onPlaceSelect, placeholder }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [ready, setReady] = useState(false);

  const stableOnChange = useCallback(onChange, [onChange]);
  const stableOnSelect = useCallback(onPlaceSelect ?? (() => {}), [onPlaceSelect]);

  useEffect(() => {
    if (!API_KEY) return;
    loadPlaces().then(() => setReady(true)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;

    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode'],
      fields: ['formatted_address', 'geometry'],
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current!.getPlace();
      const address = place.formatted_address ?? inputRef.current?.value ?? '';
      stableOnChange(address);
      const loc = place.geometry?.location;
      if (loc) stableOnSelect(loc.lat(), loc.lng(), address);
    });
  }, [ready, stableOnChange, stableOnSelect]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
    />
  );
}
